import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import { Document, Salutation } from '@vi-iot/client';

import { PATTERN } from '@common';
import { DocumentsStore, GatewayCommissioningStore, TransferInstallationFormData } from '@store';

import { EmailFormGroup, OmaFormGroup, OmaFunctionForm } from '../transfer-installation.helper';

@Component({
  selector: 'app-oma-function-form',
  templateUrl: './oma-function-form.component.html',
  styleUrls: ['./oma-function-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OmaFunctionFormComponent implements OnInit, OnDestroy {
  omaFormGroup: FormGroup<OmaFormGroup>;
  emailFormGroup: FormGroup<EmailFormGroup>;

  Salutation = Salutation;
  emailPattern = PATTERN.EMAIL;

  isGrandmaFunctionSelected = false;
  salutation: Salutation;
  dataUsageAgreementDocument$: Observable<Document>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  @Input() formData: TransferInstallationFormData;
  @Output() omaFunctionDataChange: EventEmitter<OmaFunctionForm> = new EventEmitter<
    OmaFunctionForm
  >();

  constructor(
    public gatewayCommissioningStore: GatewayCommissioningStore,
    private documentStore: DocumentsStore
  ) {}

  ngOnInit(): void {
    this.documentStore.getDataUsageAgreementDocument();
    this.salutation = this.formData.installationOwnerData?.salutation;
    this.isGrandmaFunctionSelected = this.formData.isGrandmaFunctionSelected;

    this.omaFormGroup = new FormGroup<OmaFormGroup>({
      firstName: new FormControl<string>(this.formData.installationOwnerData?.firstName, [
        Validators.required,
      ]),
      lastName: new FormControl<string>(this.formData.installationOwnerData?.lastName, [
        Validators.required,
      ]),
      isAccepted: new FormControl<boolean>(
        this.formData.installationOwnerData?.privacyPolicyAccepted,
        [Validators.required]
      ),
      isDataUsageAgreementAccepted: new FormControl<boolean>(false),
    });
    this.emailFormGroup = new FormGroup<EmailFormGroup>({
      email: new FormControl<string>(this.formData.email, [
        Validators.required,
        Validators.pattern(this.emailPattern),
      ]),
    });

    this.emitFormData();

    this.gatewayCommissioningStore.transferInstallationLoading$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((transferInstallationLoading) => {
        if (transferInstallationLoading) {
          this.omaFormGroup.disable();
          this.emailFormGroup.disable();
        } else {
          this.omaFormGroup.enable();
          this.emailFormGroup.enable();
        }
      });

    this.dataUsageAgreementDocument$ = this.documentStore.dataUsageAgreementDocument$.pipe(
      tap((document) => {
        if (document) {
          this.omaFormGroup
            .get('isDataUsageAgreementAccepted')
            ?.setValidators([Validators.required]);
        } else {
          this.omaFormGroup.get('isDataUsageAgreementAccepted')?.clearValidators();
        }
        this.emitFormData();
      })
    );

    this.omaFormGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((value) => {
      this.gatewayCommissioningStore.setFormData({
        installationOwnerData: {
          firstName: value.firstName,
          lastName: value.lastName,
          salutation: this.salutation,
          privacyPolicyAccepted: value.isAccepted,
          privacyPolicyAcceptanceUtc: new Date().toISOString(),
        },
        isGrandmaFunctionSelected: this.isGrandmaFunctionSelected,
      });

      this.emitFormData();
    });

    this.emailFormGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((value) => {
      this.gatewayCommissioningStore.setFormData({
        email: value.email,
      });

      this.emitFormData();
    });
  }

  onGrandmaFunctionToggle(isGrandmaFunctionSelected: boolean): void {
    this.isGrandmaFunctionSelected = isGrandmaFunctionSelected;

    this.onDataFormChanged();
  }

  onSalutationToggle(event: MatRadioChange): void {
    this.salutation = event.value;

    this.onDataFormChanged();
  }

  onDataFormChanged(): void {
    this.gatewayCommissioningStore.setFormData({
      installationOwnerData: {
        firstName: this.omaFormGroup.controls.firstName.value,
        lastName: this.omaFormGroup.controls.lastName.value,
        salutation: this.salutation,
        privacyPolicyAccepted: this.omaFormGroup.controls.isAccepted.value,
        privacyPolicyAcceptanceUtc: new Date().toISOString(),
      },
      isGrandmaFunctionSelected: this.isGrandmaFunctionSelected,
      email: this.emailFormGroup.controls.email.value,
    });

    this.emitFormData();
  }

  private emitFormData(): void {
    this.omaFunctionDataChange.emit({
      isGrandmaFunctionSelected: this.isGrandmaFunctionSelected,
      isOmaFunctionFormValid:
        this.omaFormGroup.valid &&
        this.omaFormGroup.controls.isAccepted.value &&
        this.omaFormGroup.controls.isDataUsageAgreementAccepted.value,
      isEmailFormValid: this.emailFormGroup.valid,
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
